import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Plus, Loader2, X, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const PIPELINE = [
  { key: 'prospection', label: 'Prospection' },
  { key: 'negociation', label: 'Négociation' },
  { key: 'actif', label: 'Actif' },
  { key: 'a_renouveler', label: 'À renouveler' },
  { key: 'expire', label: 'Expiré' },
];

const NIVEAU_BADGE = { or: 'badge-warning', argent: 'badge-neutral', bronze: 'badge-neutral', nature: 'badge-info' };

export default function AssoSponsors() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get('/sponsors'), api.get('/sponsors/stats')]);
      setList(a.data); setStats(b.data);
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const relancer = async (id) => {
    try {
      await api.post(`/sponsors/${id}/relancer`, { type: 'email', objet: 'Renouvellement de votre partenariat' });
      toast.success('Relance envoyée');
      load();
    } catch { toast.error('Erreur envoi'); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="asso-sponsors">
      <div className="flex items-center justify-between mb-8 slide-up">
        <div>
          <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Sponsors</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">{list.length} partenaires</h1>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2" data-testid="add-sponsor-btn"><Plus className="w-4 h-4" /> Ajouter</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger">
          <div className="card"><div className="label-uppercase mb-2">Actifs</div><div className="font-display text-3xl font-black text-[#0F172A]">{stats.actifs}</div></div>
          <div className="card"><div className="label-uppercase mb-2">À renouveler</div><div className="font-display text-3xl font-black text-[#0F172A]">{stats.a_renouveler}</div></div>
          <div className="card"><div className="label-uppercase mb-2">Revenus actifs</div><div className="font-display text-3xl font-black text-[#0F172A]">{Number(stats.revenus_actifs).toFixed(0)}€</div></div>
          <div className="card"><div className="label-uppercase mb-2">Total engagé</div><div className="font-display text-3xl font-black text-[#0F172A]">{Number(stats.revenus_total).toFixed(0)}€</div></div>
        </div>
      )}

      {/* Pipeline kanban */}
      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="sponsors-pipeline">
          {PIPELINE.map((col) => {
            const items = list.filter((s) => s.pipeline_etape === col.key);
            return (
              <div key={col.key} className="bg-[#F1F5F9] rounded-xl p-3 min-h-[200px]" data-testid={`pipeline-col-${col.key}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="font-display text-sm font-bold text-[#0F172A]">{col.label}</div>
                  <span className="text-xs font-bold text-[#475569]">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((s) => (
                    <div key={s.id} className="bg-white rounded-2xl border border-[#E2E8F0] p-3 hover:shadow-md transition-shadow" data-testid={`sponsor-card-${s.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-[#0F172A] text-sm truncate">{s.nom}</div>
                        <span className={`badge ${NIVEAU_BADGE[s.niveau] || 'badge-neutral'} capitalize text-[9px]`}>{s.niveau}</span>
                      </div>
                      <div className="text-xs text-[#475569] mb-2">{Number(s.montant || 0).toFixed(0)}€</div>
                      {s.contact_email && (
                        <button onClick={() => relancer(s.id)} className="w-full btn-ghost !text-xs flex items-center justify-center gap-1.5 !py-1.5" data-testid={`sponsor-relance-${s.id}`}>
                          <Send className="w-3 h-3" /> Relancer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddSponsor onClose={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddSponsor({ onClose }) {
  const [form, setForm] = useState({ nom: '', niveau: 'bronze', montant: 0, contact_nom: '', contact_email: '', date_expiration: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/sponsors', form); toast.success('Sponsor ajouté'); onClose(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="add-sponsor-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-[#0F172A]">Ajouter un sponsor</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input required type="text" placeholder="Nom de l'entreprise" className="input-base" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="add-sponsor-nom" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-base" value={form.niveau} onChange={(e) => setForm({ ...form, niveau: e.target.value })}>
              <option value="or">Or</option><option value="argent">Argent</option><option value="bronze">Bronze</option><option value="nature">Nature</option>
            </select>
            <input type="number" placeholder="Montant €" className="input-base" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} />
          </div>
          <input type="text" placeholder="Contact (nom)" className="input-base" value={form.contact_nom} onChange={(e) => setForm({ ...form, contact_nom: e.target.value })} />
          <input type="email" placeholder="Email contact" className="input-base" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          <input type="date" className="input-base" value={form.date_expiration} onChange={(e) => setForm({ ...form, date_expiration: e.target.value })} />
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="add-sponsor-submit">{saving ? 'Ajout…' : 'Ajouter'}</button>
        </div>
      </form>
    </div>
  );
}
